import React, { useState, useEffect } from 'react';
import "../../css/AllClasses.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaGraduationCap, FaSearch, FaTimes, FaChalkboardTeacher, FaUserFriends } from 'react-icons/fa';

function AllClasses() {
    const [classes, setClasses] = useState([]);
    const [filteredClasses, setFilteredClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');

    useEffect(() => {
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/classes", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch classes");
            }

            const data = await response.json();
            setClasses(data);
            setFilteredClasses(data);
        } catch (error) {
            console.error("Error fetching classes:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search and filtering
    useEffect(() => {
        let results = [...classes];
        
        // Apply search term filter
        if (searchTerm.trim() !== '') {
            results = results.filter(cls =>
                (cls.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (cls.section?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (cls.hod?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
        }
        
        // Apply selected filter
        if (selectedFilter === 'withHOD') {
            results = results.filter(cls => cls.hod && cls.hod.trim() !== '');
        } else if (selectedFilter === 'withoutHOD') {
            results = results.filter(cls => !cls.hod || cls.hod.trim() === '');
        } else if (selectedFilter === 'highCapacity') {
            results = results.filter(cls => cls.capacity && parseInt(cls.capacity) > 30);
        }
        
        setFilteredClasses(results);
    }, [searchTerm, classes, selectedFilter]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSelectedFilter('all');
    };

    // Function to get category-specific classes for a class card
    const getCategoryClass = (category) => {
        switch(category) {
            case 'Science': return 'allclasses-card-science';
            case 'Arts': return 'allclasses-card-arts';
            case 'Commerce': return 'allclasses-card-commerce';
            default: return '';
        }
    };

    // Function to determine class category based on name or section
    const getClassCategory = (cls) => {
        const name = (cls.name || '').toLowerCase();
        const section = (cls.section || '').toLowerCase();
        
        if (name.includes('science') || section.includes('science')) return 'Science';
        if (name.includes('art') || section.includes('art')) return 'Arts';
        if (name.includes('commerce') || section.includes('commerce')) return 'Commerce';
        return 'General';
    };

    return (
        <div className="allclasses-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="allclasses-header">
                <div className="allclasses-title-container">
                    <FaChalkboardTeacher className="allclasses-title-icon" />
                    <h1 className="allclasses-title">Classes Overview</h1>
                </div>

                <div className="allclasses-stats">
                    <div className="allclasses-stat-box">
                        <div className="allclasses-stat-icon">
                            <FaGraduationCap />
                        </div>
                        <div className="allclasses-stat-info">
                            <span className="allclasses-stat-number">{classes.length}</span>
                            <span className="allclasses-stat-label">Total Classes</span>
                        </div>
                    </div>
                    <div className="allclasses-stat-box">
                        <div className="allclasses-stat-icon">
                            <FaUserFriends />
                        </div>
                        <div className="allclasses-stat-info">
                            <span className="allclasses-stat-number">
                                {classes.reduce((sum, cls) => sum + (parseInt(cls.capacity) || 0), 0)}
                            </span>
                            <span className="allclasses-stat-label">Total Capacity</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="allclasses-filters">
                <div className="allclasses-search-container">
                    <FaSearch className="allclasses-search-icon" />
                    <input
                        type="text"
                        className="allclasses-search-input"
                        placeholder="Search classes by name, section or HOD..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {searchTerm && (
                        <button className="allclasses-search-clear" onClick={clearSearch}>
                            <FaTimes />
                        </button>
                    )}
                </div>

                <div className="allclasses-filter-options">
                    <select 
                        className="allclasses-filter-select"
                        value={selectedFilter}
                        onChange={(e) => setSelectedFilter(e.target.value)}
                    >
                        <option value="all">All Classes</option>
                        <option value="withHOD">With HOD</option>
                        <option value="withoutHOD">Without HOD</option>
                        <option value="highCapacity">High Capacity (30+)</option>
                    </select>
                </div>
            </div>

            <div className="allclasses-list-container">
                {filteredClasses.length === 0 ? (
                    <div className="allclasses-empty-state">
                        <FaGraduationCap className="allclasses-empty-icon" />
                        <h3>No Classes Found</h3>
                        {searchTerm || selectedFilter !== 'all' ? (
                            <p>No classes match your current filters. Try adjusting your search criteria.</p>
                        ) : (
                            <p>There are no classes available in the system.</p>
                        )}
                        {(searchTerm || selectedFilter !== 'all') && (
                            <button className="allclasses-btn allclasses-btn-primary" onClick={clearSearch}>
                                Clear Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="allclasses-grid">
                        {filteredClasses.map((cls) => {
                            const category = getClassCategory(cls);
                            const categoryClass = getCategoryClass(category);
                            
                            return (
                                <div 
                                    key={cls._id} 
                                    className={`allclasses-card ${categoryClass}`}
                                >
                                    <div className="allclasses-card-header">
                                        <div className="allclasses-card-badge">{category}</div>
                                        <h3 className="allclasses-card-title">
                                            {cls.name} - {cls.section}
                                        </h3>
                                    </div>
                                    <div className="allclasses-card-body">
                                        <div className="allclasses-card-detail">
                                            <span className="allclasses-card-label">Capacity:</span>
                                            <span className="allclasses-card-value">
                                                {cls.capacity ? `${cls.capacity} students` : 'Not specified'}
                                            </span>
                                        </div>
                                        <div className="allclasses-card-detail">
                                            <span className="allclasses-card-label">HOD:</span>
                                            <span className="allclasses-card-value">
                                                {cls.hod || 'Not assigned'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AllClasses;